import React, { useState } from "react";
import { ApplicationListController } from "./controller";
import "./index.scss";
import { Routes } from "../../../common/Routes";
import { AuthZTable, defaultPageSize, defaultPageSizeOptions } from "../../../components/authZTable";
import { LinkSpan } from "../../../components/linkSpan";
import { CollapsibleList } from "../../../components/collapsibleList";
import { Select } from "antd";
import ApplicationListActions from "../../../components/ApplicationListActions";
import { checkIfUserIsGlobalAccessApprover } from "../../../common/CheckIfUserIsGlobalAccessApprover";
import { AsyncActionButtonOrLink } from "../../../components/asyncActionButtonOrLink";
import { isNullOrEmptyString, isNullOrUndefined } from "../../../common/utils";
import {
  ApplicationAction,
  sendApplicationEvent,
  sendTableEvent,
  TableAction,
} from "../../../components/matomoClient";
import { TooltipMessage } from "../../../common/data/TooltipMsg";
import { ModalMessage } from "../../../common/data/ModalMsg";
import {
  allApplicationsTable,
  ApplicationInReviewStatus,
  ApplicationNotInReviewStatus,
  complianceTags,
  selectComplianceTagKey,
} from "./model";
import { NonSSOLabel, SSOLabel } from "../../../components/appLabel";
import { formatLocalDateToUtc } from "../../../common/utils/TimeUtils";
import { IsFeatureEnabledOrNot } from "src/FeatureToggles/FeatureEnabledOrNot";
import { ToggleList } from "src/FeatureToggles/toggleList";
import { ReviewActions } from "../../../components/ReviewActions";
import { UserGuideLink } from "../../user-guide/user-guide-link";
import { MultiSelect } from "../../../components/multiSelect";
import { iif } from "../../../common/utils/functions";

export const ApplicationList = ({ selectedDisplayColumns }) => {
  let complianceTagFilterFromStorage = localStorage.getItem(selectComplianceTagKey);
  const [complianceTagFilter, setComplianceTagFilter] = useState(
    complianceTagFilterFromStorage ? complianceTagFilterFromStorage : complianceTags.all,
  );
  const [selectedColumns, setSelectedColumns] = useState(selectedDisplayColumns ?? []);

  const isReviewCycleEnabled = IsFeatureEnabledOrNot(ToggleList.ReviewCycle);
  const { Option } = Select;
  const {
    dataSource,
    isLoading,
    sendGetLatestUserAccessDataEmail,
    isMyTasksColumnVisible,
    canDisplayTasksCount,
  } = ApplicationListController();
  const [size, setSize] = useState(defaultPageSize);
  const [page, setPage] = useState(1);

  const onPageChange = (current) => {
    setPage(current);
  };

  function changePageSize(size) {
    setSize(size);
  }

  function requestLatestData(appInfo) {
    let okRequestUserAccessDataCallback = async () => {
      sendApplicationEvent(ApplicationAction.RequestUserAccess, appInfo.name);
      await sendGetLatestUserAccessDataEmail(appInfo.id, appInfo.gitsProductTeamEmail);
    };

    return (
      <AsyncActionButtonOrLink
        type="link"
        linkText="Request User Access Data"
        tooltipText={TooltipMessage.requestUserAccessData}
        modalProps={{
          content: ModalMessage.requestUserAccessData.getConfirmationMsg(
            appInfo?.gitsProductTeamEmail,
          ),
          title: `${ModalMessage.requestUserAccessData.title} - ${appInfo.name}`,
          className: "start-modal",
        }}
        modalOkButtonProps={{
          okCallback: okRequestUserAccessDataCallback,
          okText: ModalMessage.requestUserAccessData.okText,
        }}
      />
    );
  }

  function showReviewStatus(text, record) {
    return text === ApplicationInReviewStatus ? (
      <LinkSpan
        keyId={record.id}
        to={`${Routes.Applications.path}/${record.uniqueName}/activeReview?reviewName=${record.currentActiveReviewName}&reviewId=${record.currentActiveReviewId}`}
        text={text}
        matomoContext={`${record.name}`}
      />
    ) : isNullOrUndefined(text) ? (
      ApplicationNotInReviewStatus
    ) : (
      text
    );
  }

  const optionalColumns = ["Business Functions", "Global Access Approver(s)"];

  function renderMyTasksColumn() {
    return isMyTasksColumnVisible
      ? [{
        title: "My Tasks",
        width: "120px",
        dataIndex: "review",
        key: "review",
        render: (_text, record) => {
          if (canDisplayTasksCount(record)) {
            const review = record.recentReview;
            const noOfTasksAssignedToUser = review.noOfTasksAssignedToUser;

            function renderTasksCountFor(status, count) {
              return (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "0.125rem" }}>
                  <div>{status} </div>
                  <div>{count}</div>
                </div>
              );
            }

            const appUrlPath = `${Routes.Applications.path}/${record.uniqueName}`;
            const url = review.status === "In Progress"
              ? appUrlPath
              : `${appUrlPath}/lastReviewed?reviewId=${review.id}&reviewName=${review.name}`;

            return (
              <LinkSpan
                data-testid={`my-tasks-${record.id}`}
                keyId={record.id}
                to={url}
                matomoContext={`${record.name}`}
                matomoFn={() => {
                  sendApplicationEvent(ApplicationAction.ClickMyTasksCount);
                }}
                text={(
                  <div>
                    {renderTasksCountFor("Pending", noOfTasksAssignedToUser.pending)}
                    {renderTasksCountFor("Completed", noOfTasksAssignedToUser.completed)}
                    {renderTasksCountFor("Total", noOfTasksAssignedToUser.total)}
                  </div>
                )}
              />
            );
          }
          return <div data-testid={`my-tasks-${record.id}`}>Not Applicable</div>;
        },
        searchEnabled: false,
        sortEnabled: false,
      }]
      : [];
  }

  let columns = [
    {
      title: "Application",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div className={"applicationName"}>
          {record.complianceTags.includes(complianceTags.sso) ? <SSOLabel /> : <NonSSOLabel />}
          <span>
                    <LinkSpan
                      keyId={record.id}
                      to={`${Routes.Applications.path}/${record.uniqueName}`}
                      text={text}
                      matomoContext="Application"
                    />&nbsp;
            {
              (record.complianceTags.includes(complianceTags.itgc) && isReviewCycleEnabled) ?
                <sup className={"itgc-tag"}>
                  ITGC
                </sup> : <></>
            }
          </span>
        </div>
      ),
      width: "160px",
      searchEnabled: true,
      sortEnabled: true,
    },
    {
      title: "Business Functions",
      dataIndex: "businessFunctions",
      key: "businessFunctions",
      width: "120px",
      searchEnabled: true,
      sortEnabled: false,
    },
    {
      title: "No of Users",
      dataIndex: "totalReviewee",
      key: "totalReviewee",
      width: "80px",
      sorter: (recordA, recordB) => {
        return recordA.totalReviewee - recordB.totalReviewee;
      },
      searchEnabled: false,
      sortEnabled: true,
    },
    {
      title: "Global Access Approver(s)",
      dataIndex: "globalAccessApproverDisplay",
      key: "globalAccessApprovers",
      render: (globalAccessApproverDisplay) => (
        <CollapsibleList className={"globalAccessApproverList"}>
          {globalAccessApproverDisplay.map((gaaDisplay) => (
            <div key={gaaDisplay} style={{ paddingBottom: "8px" }}>{gaaDisplay}</div>
          ))}
        </CollapsibleList>
      ),
      width: "200px",
      searchEnabled: true,
      sortEnabled: false,
    },
    {
      title: "Last Imported",
      dataIndex: "lastImportedOn",
      key: "lastImportedOn",
      width: "132px",
      render: (text, record) => {
        const isReviewable = record?.isReviewable ?? false;
        return (
          <div>
            {text}
            {(checkIfUserIsGlobalAccessApprover(record) && !isReviewable) ? requestLatestData(record) : null}
          </div>
        );
      },
      sorter: (recordA, recordB) => {
        let lastImportedOnAUtc = formatLocalDateToUtc(recordA.lastImportedOn);
        let lastImportedOnBUtc = formatLocalDateToUtc(recordB.lastImportedOn);
        return lastImportedOnAUtc - lastImportedOnBUtc;
      },
      searchEnabled: false,
      sortEnabled: true,
    },
    {
      title: "Last Reviewed",
      dataIndex: "lastReviewedOn",
      key: "lastReviewedOn",
      width: "120px",
      render: (text, record) => (
        <div>
          {text}
          <div>
            {!isNullOrEmptyString(text) ? (
              <LinkSpan
                keyId={record.id}
                to={`${Routes.Applications.path}/${record.uniqueName}/lastReviewed?reviewId=${record.lastReviewId}&reviewName=${record.lastReviewName}`}
                matomoContext={`${record.name}`}
                text={"Review Log"}
              />
            ) : null}
          </div>
        </div>
      ),
      sorter: (recordA, recordB) => {
        let lastReviewedOnAUtc = formatLocalDateToUtc(recordA.lastReviewedOn);
        let lastReviewedOnBUtc = formatLocalDateToUtc(recordB.lastReviewedOn);
        return lastReviewedOnAUtc - lastReviewedOnBUtc;
      },
      searchEnabled: false,
      sortEnabled: true,
    },
    ...renderMyTasksColumn(),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => (
        <div>
          {record.isReviewable === false ? "Not reviewable" : showReviewStatus(text, record)}
        </div>
      ),
      width: "128px",
      searchEnabled: true,
      sortEnabled: true,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_text, record) => (
        <div>
          <ApplicationListActions
            applicationUniqueName={record.uniqueName}
            applicationName={record.name}
            applicationURL={record.accessTypeLink}
          />
        </div>
      ),
      width: "6.5%",
      searchEnabled: false,
      sortEnabled: false,
    },
    {
      title: "Review Actions",
      key: "review-cycle",
      render: (_text, record) => (
        <ReviewActions appDetail={record} />
      ),
      width: "8%",
      searchEnabled: false,
      sortEnabled: false,
    },
  ];

  function handleSelectChange(value) {
    setComplianceTagFilter(value);
    localStorage.setItem(selectComplianceTagKey, value);
  }

  const filteredDataSource = dataSource?.filter((appInfo) => {
    if (complianceTagFilter === complianceTags.all) {
      return true;
    } else {
      return appInfo.complianceTags.includes(complianceTagFilter);
    }
  });

  if (!isReviewCycleEnabled) {
    columns = columns.filter((column) => column.key !== ToggleList.ReviewCycle);
  } else {
    columns = columns.filter((column) => column.key !== "actions");
  }
  iif(function displayOnlyTheSelectedColumns() {
    columns = columns.filter((column) => !(optionalColumns.includes(column.title)) || selectedColumns.includes(column.title));
  });

  return (
    <div className="application-container">
      <div className={"contentTitle"}>
        Access Reviews
        <UserGuideLink
          linkText={"Click here to access the Access Review User Guide"}
          isVisible={isReviewCycleEnabled}
          processDocumentActiveTab="access-review"
        />
      </div>
      <div className={"selectContainer"}>
        <div>Type:</div>
        <Select
          defaultValue={complianceTagFilter}
          className={"selectBody"}
          onChange={handleSelectChange}
        >
          <Option value={complianceTags.all}>{complianceTags.all}</Option>
          <Option value={complianceTags.itgc}>{complianceTags.itgc}</Option>
          <Option value={complianceTags.others}>{complianceTags.others}</Option>
        </Select>
      </div>
      <div className={"applicationList"}>
        <AuthZTable
          id={allApplicationsTable.id}
          name={"appList"}
          rowKey={"id"}
          columns={columns}
          dataSource={filteredDataSource}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            pageSize: size,
            pageSizeOptions: defaultPageSizeOptions,
            showTotal: function renderDisplayColumnsWithTotalCount(total, range) {
              return (
                <div style={{ display: "flex", gap: "0.25rem" }}>
                  <MultiSelect
                    placeHolder={"Select Columns"}
                    options={optionalColumns}
                    selected={selectedColumns}
                    onSelected={(value) => {
                      setSelectedColumns((value));
                      sendTableEvent(TableAction.SelectColumn, value);
                    }}
                    pinned={[]}
                  />
                  <div>{range[0]}-{range[1]} of {total} items</div>
                </div>
              );
            },
            current: page,
            onShowSizeChange: (current, pageSize) => changePageSize(pageSize, current),
            onChange: (current) => onPageChange(current),
          }}
          scroll={{ x: "max-content", y: "calc(100vh - 408px)" }}
          loading={isLoading}
        />
      </div>
    </div>
  );
};
